import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, HostListener, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { CheckboxModule } from 'primeng/checkbox';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { environment } from '../../../../environments/environment';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { CustomerService } from '../../customers/services/customer.service';
import { AuthService } from '../../../core/auth/auth.service';
import { canAccess, DEPARTMENT, ROLE_LEVEL } from '../../../core/auth/access-control';
import { canModifyBooking } from '../../../core/auth/booking-access';
import { BookingListItem, BookingService } from '../../bookings/services/booking.service';
import { GroupBookingService } from '../../bookings/services/group-booking.service';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import {
  TourAdministratorItem,
  TourExpenseItem,
  TourItem,
  TourService,
  TourStatus,
  TourType,
  TourFinancialAdjustmentRequest,
  TourFinancialAdjustmentItem,
} from '../services/tour.service';

interface TourAdministratorView {
  id: string;
  userId: string;
  name: string;
  responsibility: string;
  isPrimary: boolean;
}

interface TourExpenseView {
  id: string;
  expenseNo: string;
  name: string;
  description: string;
  amount: number;
  estimatedAmount: number;
  actualTransferAmount: number | null;
  paidAmount: number;
  attachmentUrl: string | null;
  receiptUrl: string | null;
  requester: string;
  department: string;
  createdAt: string;
  source: string;
  status: string;
}

interface TourAdvanceApprovalView {
  id: string;
  requestNo: string;
  requestedAt: string;
  requestedBy: string;
  reason: string;
  amount: number;
  tourPriceDelta: number;
  expenseBudgetDelta: number;
  customerChargeDelta: number;
  companyAdvanceAmount: number;
  advanceAttachmentPath: string | null;
  approver: string | null;
  status: string;
}

interface TourCommissionView {
  id: string;
  commissionNo: string;
  administratorId: string;
  administrator: string;
  amount: number;
  paidAmount: number;
  recoveryOutstanding: number;
  recoveryDeduction: number;
  netPaymentAmount: number;
  commissionRate: number;
  pax: number;
  status: string;
  note: string;
  recoveries: TourCommissionRecoveryView[];
}

interface TourCommissionRecoveryView {
  id: string;
  bookingNo: string;
  refundNo: string;
  appliedAmount: number;
  recoveryAmount: number;
  settledAmount: number;
  remainingAmount: number;
  status: 'PENDING' | 'PARTIAL' | 'SETTLED' | 'WAIVED' | 'CANCELLED';
  reason: string;
  createdAt: Date;
  applications: Array<{
    id: string;
    targetCode: string;
    amount: number;
    status: 'RESERVED' | 'APPLIED' | 'RELEASED';
  }>;
}

interface TourBookingView {
  id: string;
  bookingNo: string;
  customerName: string;
  bookingType: TourType;
  pax: number;
  totalAmount: number;
  discount: number;
  paidAmount: number;
  withholdingTaxAmount: number;
  customerCreditCardFee: number;
  companyCreditCardFee: number;
  refundedAmount: number;
  activeRefundAmount: number;
  status: string;
  paymentStatus: string;
}

interface TourDetailView {
  id: string;
  tourNo: string;
  code: string;
  tourName: string;
  type: TourType;
  travelDate: string;
  returnDate: string | null;
  requiredPax: number;
  currentPax: number;
  bookingCount: number;
  tourPrice: number;
  tourTotalAmount: number;
  estimatedExpense: number;
  saleCommission: number;
  adminCommissionPercent: number;
  programDetail: string | null;
  note: string | null;
  profileUrl: string | null;
  pdfUrl: string | null;
  status: TourStatus;
  createdById: string;
  createdByName: string;
  administrators: TourAdministratorView[];
  expenses: TourExpenseView[];
  bookings: TourBookingView[];
}

function currentBangkokDateKey(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values['year']}-${values['month']}-${values['day']}`;
}

function hasPassedReturnDate(returnDate: string | null): boolean {
  const returnDateKey = returnDate?.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  return Boolean(returnDateKey && currentBangkokDateKey() > returnDateKey);
}

@Component({
  selector: 'app-tour-detail-page',
  imports: [
    CheckboxModule,
    DatePipe,
    DecimalPipe,
    FileUploadModule,
    InputNumberModule,
    InputTextModule,
    LucideAngularModule,
    ReactiveFormsModule,
    RefreshButtonComponent,
    RouterLink,
    SelectModule,
    StatusBadge,
    TableModule,
    TextareaModule,
  ],
  templateUrl: './tour-detail-page.component.html',
})
export class TourDetailPageComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly bookingService = inject(BookingService);
  private readonly groupBookingService = inject(GroupBookingService);
  readonly accessibleBookings = signal<BookingListItem[]>([]);
  readonly editingBooking = signal<BookingListItem | null>(null);
  readonly isDeletingBooking = signal(false);
  readonly visibleBookings = computed<TourBookingView[]>(() => {
    const tour = this.tour();
    return tour?.bookings ?? [];
  });

  private readonly route = inject(ActivatedRoute);
  private readonly tourService = inject(TourService);
  private readonly overlay = inject(GlobalOverlayService);
  private readonly fb = inject(FormBuilder);
  private readonly customerService = inject(CustomerService);
  readonly domesticCustomerOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly isSavingDomesticEdit = signal(false);
  readonly domesticEditForm = this.fb.nonNullable.group({
    customerId: ['', Validators.required],
    travelerCount: [1, [Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]],
    discount: [0, [Validators.required, Validators.min(0)]],
  });

  private readonly assetBaseUrl = environment.apiUrl.replace(/\/api\/?$/, '');

  readonly tour = signal<TourDetailView | null>(null);
  readonly isRefreshing = signal(false);
  readonly isRefreshButtonHidden = signal(true);
  readonly isClosingCase = signal(false);
  readonly isTourClosed = computed(() => this.tour()?.status === 'COMPLETED');

  /**
   * ยอดรวมคอมมิชชั่นและกำไรเป็นข้อมูลภายในที่ไม่แสดงให้พนักงาน Sales role 3
   * อัตราคอม Sale ต่อคนและอัตราคอมผู้ดูแลยังแสดงในข้อมูลทัวร์ให้ผู้มีสิทธิ์เข้าดูทุกคน
   */
  readonly canViewCommissionDetails = computed(() => {
    const user = this.auth.user();
    return !(
      user?.role.level === ROLE_LEVEL.EMPLOYEE &&
      user.department.code === DEPARTMENT.SALES
    );
  });

  readonly canManageTourExpenses = computed(() =>
    !this.isTourClosed() &&
    canAccess(this.auth.user(), {
      levels: [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE],
      departments: [DEPARTMENT.SALES],
    }),
  );
  readonly canManagePrimaryTourData = computed(() => {
    const user = this.auth.user();
    if (!this.canManageTourExpenses() || !user) return false;
    if (user.role.level === ROLE_LEVEL.ADMIN || user.role.level === ROLE_LEVEL.MANAGER) return true;
    return this.tour()?.administrators.some(
      (administrator) => administrator.userId === user.id && administrator.isPrimary,
    ) ?? false;
  });
  readonly canAddTourAdministrator = computed(() => {
    const user = this.auth.user();
    const tour = this.tour();
    if (!user || !tour || ['COMPLETED', 'CANCELLED', 'DELETED'].includes(tour.status)) return false;
    return (
      user.role.level === ROLE_LEVEL.ADMIN ||
      (user.role.level === ROLE_LEVEL.MANAGER && user.department.code === DEPARTMENT.MARKETING) ||
      tour.createdById === user.id ||
      tour.administrators.some(
        (administrator) => administrator.userId === user.id && administrator.isPrimary,
      )
    );
  });
  readonly canCloseTourCase = computed(() => {
    const tour = this.tour();
    const user = this.auth.user();
    return Boolean(
      tour &&
        user &&
        (user.role.level === ROLE_LEVEL.ADMIN || hasPassedReturnDate(tour.returnDate)) &&
        this.canAddTourAdministrator() &&
        !['COMPLETED', 'CANCELLED', 'DELETED'].includes(tour.status),
    );
  });
  readonly canShowCloseTourCase = computed(() => {
    const tour = this.tour();
    const user = this.auth.user();
    return Boolean(
      tour &&
        user &&
        (user.role.level === ROLE_LEVEL.ADMIN || hasPassedReturnDate(tour.returnDate)),
    );
  });

  readonly advanceApprovals = signal<TourAdvanceApprovalView[]>([]);
  readonly isDeletingFinancialAdjustment = signal(false);

  readonly approvedTourPriceDelta = computed(() =>
    this.advanceApprovals()
      .filter((request) => request.status === 'APPROVED')
      .reduce((total, request) => total + request.tourPriceDelta, 0),
  );

  readonly originalTourTotalAmount = computed(() =>
    Math.max((this.tour()?.tourTotalAmount ?? 0) - this.approvedTourPriceDelta(), 0),
  );

  readonly approvedCustomerChargeDelta = computed(() =>
    this.advanceApprovals()
      .filter((request) => request.status === 'APPROVED')
      .reduce((total, request) => total + request.customerChargeDelta, 0),
  );

  readonly approvedExpenseBudgetDelta = computed(() =>
    this.advanceApprovals()
      .filter((request) => request.status === 'APPROVED')
      .reduce((total, request) => total + request.expenseBudgetDelta, 0),
  );

  readonly approvedCompanyAdvanceAmount = computed(() =>
    this.advanceApprovals()
      .filter((request) => request.status === 'APPROVED')
      .reduce((total, request) => total + request.companyAdvanceAmount, 0),
  );

  readonly originalEstimatedExpense = computed(() =>
    Math.max((this.tour()?.estimatedExpense ?? 0) - this.approvedExpenseBudgetDelta(), 0),
  );

  readonly tourCommissionRequests = signal<TourCommissionView[]>([]);
  readonly selectedTourCommission = signal<TourCommissionView | null>(null);
  readonly selectedTourCommissionRecovery = signal<TourCommissionView | null>(null);
  readonly isSubmittingTourCommission = signal(false);

  readonly canRequestTourCommission = computed(() => {
    const user = this.auth.user();
    const tour = this.tour();
    if (!user || !tour || tour.status !== 'COMPLETED') return false;
    return (
      user.role.level === ROLE_LEVEL.ADMIN ||
      canAccess(user, {
        levels: [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE],
        departments: [DEPARTMENT.SALES, DEPARTMENT.MARKETING],
      })
    );
  });

  canSubmitTourCommission(commission: TourCommissionView): boolean {
    const user = this.auth.user();
    return Boolean(
      user &&
        this.canRequestTourCommission() &&
        commission.status === 'DRAFT' &&
        (user.role.level <= ROLE_LEVEL.MANAGER || commission.administratorId === user.id),
    );
  }

  openTourCommissionModal(commission: TourCommissionView): void {
    if (!this.canSubmitTourCommission(commission)) return;
    this.selectedTourCommission.set(commission);
  }

  closeTourCommissionModal(): void {
    if (this.isSubmittingTourCommission()) return;
    this.selectedTourCommission.set(null);
  }

  openTourCommissionRecoveryHistory(commission: TourCommissionView): void {
    if (commission.recoveries.length === 0) return;
    this.selectedTourCommissionRecovery.set(commission);
  }

  closeTourCommissionRecoveryHistory(): void {
    this.selectedTourCommissionRecovery.set(null);
  }

  tourCommissionRecoveryTotal(
    commission: TourCommissionView,
    field: 'appliedAmount' | 'recoveryAmount' | 'settledAmount' | 'remainingAmount',
  ): number {
    return commission.recoveries.reduce((total, recovery) => total + recovery[field], 0);
  }

  tourCommissionRecoveryStatusLabel(status: TourCommissionRecoveryView['status']): string {
    return {
      PENDING: 'รอหักคืน',
      PARTIAL: 'หักคืนบางส่วน',
      SETTLED: 'หักคืนครบแล้ว',
      WAIVED: 'ยกเว้นการหักคืน',
      CANCELLED: 'ยกเลิกรายการ',
    }[status];
  }

  tourCommissionRecoveryStatusClass(status: TourCommissionRecoveryView['status']): string {
    return {
      PENDING: 'bg-red-50 text-red-700 ring-red-200',
      PARTIAL: 'bg-amber-50 text-amber-700 ring-amber-200',
      SETTLED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
      WAIVED: 'bg-slate-100 text-slate-600 ring-slate-200',
      CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
    }[status];
  }

  async submitTourCommission(): Promise<void> {
    const commission = this.selectedTourCommission();
    const tour = this.tour();
    if (
      !commission ||
      !tour ||
      !this.canSubmitTourCommission(commission) ||
      this.isSubmittingTourCommission()
    ) return;

    this.isSubmittingTourCommission.set(true);
    try {
      await this.tourService.submitCommission(tour.id, commission.id);
      this.selectedTourCommission.set(null);
      await this.loadTour(tour.id);
      this.overlay.success('ส่งเบิกค่าคอมมิชชั่นสำเร็จ', 'ส่งรายการให้หัวหน้าตรวจสอบแล้ว');
    } catch (error: unknown) {
      this.overlay.error(
        'ส่งเบิกค่าคอมมิชชั่นไม่สำเร็จ',
        this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'),
      );
    } finally {
      this.isSubmittingTourCommission.set(false);
    }
  }

  adjustmentAmount(value: number | string | null | undefined): number {
    return Number(value ?? 0);
  }

  openAdjustmentAttachment(path: string | null): void {
    if (!path) return;
    window.open(`${this.assetBaseUrl}${path}`, '_blank', 'noopener,noreferrer');
  }

  async deleteFinancialAdjustment(approval: TourAdvanceApprovalView): Promise<void> {
    const tour = this.tour();
    if (
      !tour ||
      !this.canManagePrimaryTourData() ||
      approval.status !== 'REJECTED' ||
      this.isDeletingFinancialAdjustment()
    ) return;

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบคำขอ',
      message: `ต้องการลบคำขอ ${approval.requestNo} ที่ไม่ผ่านใช่หรือไม่?`,
      confirmText: 'ลบคำขอ',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.isDeletingFinancialAdjustment.set(true);
    this.overlay.showLoading('กำลังลบคำขอปรับวงเงิน...');
    try {
      await this.tourService.deleteFinancialAdjustment(tour.id, approval.id);
      this.advanceApprovals.update((items) => items.filter((item) => item.id !== approval.id));
      this.overlay.success('ลบคำขอสำเร็จ');
    } catch (error: unknown) {
      this.overlay.error('ลบคำขอไม่สำเร็จ', this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'));
    } finally {
      this.isDeletingFinancialAdjustment.set(false);
      this.overlay.hideLoading();
    }
  }

  readonly isAddAdministratorOpen = signal(false);
  readonly administratorOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly isLoadingAdministratorOptions = signal(false);
  readonly isSavingAdministrator = signal(false);

  readonly administratorForm = this.fb.nonNullable.group({
    userId: ['', Validators.required],
    responsibility: ['', Validators.maxLength(100)],
    isPrimary: [false],
  });

  // --- Modal เบิกค่าใช้จ่าย (UI เท่านั้น ยังไม่เชื่อมหลังบ้าน) ---
  readonly isExpenseModalOpen = signal(false);
  readonly isSavingExpense = signal(false);
  readonly editingExpenseId = signal<string | null>(null);
  readonly editingExpenseSource = signal<string | null>(null);
  readonly purchaseOrderFileName = signal('');
  readonly purchaseOrderFile = signal<File | null>(null);
  readonly existingPurchaseOrderUrl = signal<string | null>(null);
  readonly selectedReceiptExpense = signal<TourExpenseView | null>(null);
  readonly expenseReceiptFile = signal<File | null>(null);
  readonly expenseReceiptFileName = signal('');
  readonly isSavingExpenseReceipt = signal(false);

  // TODO: แทนที่ด้วยประเภทค่าใช้จ่ายจาก API เมื่อเชื่อมหลังบ้าน
  readonly expenseTypeOptions = signal<string[]>([]);

  readonly expenseForm = this.fb.nonNullable.group({
    expenseNameSnapshot: ['', Validators.required],
    estimatedAmount: this.fb.control<number | null>({ value: null, disabled: true }),
    actualTransferAmount: this.fb.control<number | null>(null, [
      Validators.required,
      Validators.min(1),
    ]),
    description: ['', Validators.required],
  });

  // --- Modal สร้างคำขอปรับวงเงินและเงินสำรอง (UI เท่านั้น ยังไม่เชื่อมหลังบ้าน) ---
  readonly isFinancialAdjustmentModalOpen = signal(false);
  readonly advanceAttachmentFileName = signal('');
  readonly advanceAttachmentFile = signal<File | null>(null);

  readonly financialAdjustmentForm = this.fb.nonNullable.group({
    expenseBudgetDelta: this.fb.control<number | null>(null),
    adjustExpenseBudget: [false],
    adjustBookingCharges: [false],
    adjustTourPrice: [false],
    tourPriceDelta: this.fb.control<number | null>(null),
    requestAdvance: [false],
    bookingCharges: this.fb.array<ReturnType<typeof this.createBookingChargeForm>>([]),
    companyAdvanceAmount: this.fb.control<number | null>(null),
    reason: ['', Validators.required],
  });

  readonly totalExpense = computed(() =>
    (this.tour()?.expenses ?? []).reduce((total, expense) => total + expense.amount, 0),
  );

  readonly verifiedExpenseTotal = computed(() =>
    (this.tour()?.expenses ?? [])
      .reduce((total, expense) => total + expense.paidAmount, 0),
  );

  readonly activeExpenseTotal = computed(() =>
    (this.tour()?.expenses ?? [])
      .filter((expense) => !['REJECTED', 'CANCELLED', 'DELETED'].includes(expense.status))
      .reduce(
        (total, expense) => total + (expense.actualTransferAmount ?? expense.amount),
        0,
      ),
  );

  readonly activeExpenseFundingTotal = computed(() =>
    (this.tour()?.expenses ?? [])
      .filter((expense) => !['REJECTED', 'CANCELLED', 'DELETED'].includes(expense.status))
      .reduce((total, expense) => total + this.expenseFundingAmount(expense), 0),
  );

  readonly expenseBudgetRemaining = computed(() =>
    Math.max((this.tour()?.estimatedExpense ?? 0) - this.activeExpenseTotal(), 0),
  );

  readonly availableExpenseAmount = computed(() =>
    Math.max(
      this.verifiedBookingPaymentTotal() +
        this.approvedCompanyAdvanceAmount() -
        this.activeBookingRefundTotal() -
        this.activeExpenseFundingTotal(),
      0,
    ),
  );

  readonly hasBlockingCommissionForExpense = computed(() =>
    this.tourCommissionRequests().some(
      (commission) => !['REJECTED', 'CANCELLED', 'DELETED'].includes(commission.status),
    ),
  );

  readonly canCreateExpense = computed(
    () =>
      this.canManageTourExpenses() &&
      this.tour()?.status !== 'DELETED' &&
      !this.hasBlockingCommissionForExpense() &&
      this.availableExpenseAmount() > 0 &&
      this.expenseBudgetRemaining() > 0,
  );

  readonly bookingSalesTotal = computed(() => {
    const tour = this.tour();
    return tour ? tour.tourPrice * tour.currentPax : 0;
  });

  readonly bookingsIncludedInTotals = computed(() =>
    (this.tour()?.bookings ?? []).filter((booking) => !this.isRefundBooking(booking)),
  );

  readonly totalBookingDiscount = computed(() =>
    this.bookingsIncludedInTotals().reduce((total, booking) => total + booking.discount, 0),
  );

  readonly bookingNetSalesTotal = computed(() =>
    Math.max(
      this.bookingSalesTotal() -
        this.totalBookingDiscount() +
        this.approvedCustomerChargeDelta(),
      0,
    ),
  );

  readonly bookingOutstandingTotal = computed(() =>
    Math.max(
      this.bookingNetSalesTotal() -
        this.verifiedBookingPaymentTotal() -
        this.verifiedBookingWithholdingTaxTotal(),
      0,
    ),
  );

  readonly bookingAvailableBalanceTotal = computed(() =>
    Math.max(
      this.verifiedBookingPaymentTotal() -
        this.paidRefundTotal() -
        this.verifiedExpenseTotal() -
        this.bookingCompanyCreditCardFeeTotal(),
      0,
    ),
  );

  readonly verifiedBookingPaymentTotal = computed(() =>
    this.bookingsIncludedInTotals().reduce((total, booking) => total + booking.paidAmount, 0),
  );

  readonly verifiedBookingWithholdingTaxTotal = computed(() =>
    this.bookingsIncludedInTotals().reduce(
      (total, booking) => total + booking.withholdingTaxAmount,
      0,
    ),
  );

  readonly paidRefundTotal = computed(() =>
    this.bookingsIncludedInTotals().reduce(
      (total, booking) => total + booking.refundedAmount,
      0,
    ),
  );

  readonly activeBookingRefundTotal = computed(() =>
    this.bookingsIncludedInTotals().reduce(
      (total, booking) => total + booking.activeRefundAmount,
      0,
    ),
  );

  readonly bookingCustomerCreditCardFeeTotal = computed(() =>
    this.bookingsIncludedInTotals().reduce(
      (total, booking) => total + booking.customerCreditCardFee,
      0,
    ),
  );

  readonly bookingCompanyCreditCardFeeTotal = computed(() =>
    this.bookingsIncludedInTotals().reduce(
      (total, booking) => total + booking.companyCreditCardFee,
      0,
    ),
  );

  readonly adminCommissionTotal = computed(() => {
    const tour = this.tour();
    return tour
      ? (Math.max(this.bookingAvailableBalanceTotal() - this.saleCommissionTotal(), 0) *
          tour.adminCommissionPercent) /
          100
      : 0;
  });

  readonly saleCommissionTotal = computed(() => {
    const tour = this.tour();
    return tour ? tour.saleCommission * tour.currentPax : 0;
  });

  readonly totalCommission = computed(() => {
    const tour = this.tour();
    if (!tour) return 0;
    return this.saleCommissionTotal() + this.adminCommissionTotal();
  });

  readonly estimatedProfit = computed(() => {
    if (!this.tour()) return 0;
    return (
      this.bookingAvailableBalanceTotal() -
      this.saleCommissionTotal() -
      this.adminCommissionTotal()
    );
  });

  async ngOnInit(): Promise<void> {
    const tourId = this.route.snapshot.paramMap.get('id');
    if (!tourId) {
      return;
    }
    await this.loadTour(tourId);
  }

  async refreshTourDetails(): Promise<void> {
    const tourId = this.route.snapshot.paramMap.get('id');
    if (!tourId || this.isRefreshing()) return;

    this.isRefreshing.set(true);
    try {
      await this.loadTour(tourId);
    } finally {
      this.isRefreshing.set(false);
    }
  }

  async closeTourCase(): Promise<void> {
    const tour = this.tour();
    if (!tour || !this.canCloseTourCase() || this.isClosingCase()) return;

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการปิดเคสทัวร์',
      message: 'ระบบจะตรวจสอบ Booking และรายการการเงินทั้งหมดก่อนสร้างค่าคอมมิชชั่นร่างให้ผู้ดูแลทัวร์',
      confirmText: 'ปิดเคส',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.isClosingCase.set(true);
    this.overlay.showLoading('กำลังตรวจสอบและปิดเคสทัวร์...');
    try {
      const result = await this.tourService.closeCase(tour.id);
      await this.loadTour(tour.id);
      this.overlay.success('ปิดเคสทัวร์สำเร็จ', `สร้างค่าคอมมิชชั่นร่าง ${result.commissionCount} รายการแล้ว`);
    } catch (error: unknown) {
      this.overlay.error('ปิดเคสทัวร์ไม่สำเร็จ', this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'));
    } finally {
      this.isClosingCase.set(false);
      this.overlay.hideLoading();
    }
  }

  private async loadTour(tourId: string): Promise<void> {
    this.overlay.showLoading('กำลังโหลดรายละเอียดทัวร์...');
    try {
      const [tour, expenses, adjustments] = await Promise.all([
        this.tourService.getById(tourId),
        this.tourService.getExpenses(tourId),
        this.tourService.getFinancialAdjustments(tourId),
      ]);
      let accessibleBookings: BookingListItem[] = [];
      try {
        if (tour.tourType === 'GROUP_TOUR') {
          const results = await Promise.allSettled(
            tour.bookings.map((booking) => this.groupBookingService.getById(booking.id)),
          );
          accessibleBookings = results
            .filter((result): result is PromiseFulfilledResult<BookingListItem | undefined> => result.status === 'fulfilled')
            .map((result) => result.value)
            .filter((booking): booking is BookingListItem => !!booking);
        } else {
          accessibleBookings = (await this.bookingService.getAll('JOIN_TOUR'))
            .filter((booking) => booking.tourId === tourId);
        }
      } catch {
        // การโหลดรายการ Booking เพื่อเปิดปุ่มจัดการไม่ควรทำให้ผู้มีสิทธิ์ดู Tour เปิดหน้าไม่ได้
      }
      this.accessibleBookings.set(accessibleBookings);
      this.tour.set(this.toTourDetailView(tour, expenses));
      const recoveryOutstandingByRecipient = new Map(
        (tour.commissionRecoveryBalances ?? []).map((balance) => [
          balance.recipientUserId,
          Number(balance.outstandingAmount),
        ]),
      );
      this.tourCommissionRequests.set(
        tour.tourCommissions.map((commission) => {
          const amount = Number(commission.amount);
          const recoveryOutstanding = recoveryOutstandingByRecipient.get(commission.administratorId) ?? 0;
          const recoveryDeduction = Math.min(amount, recoveryOutstanding);
          return {
          id: commission.id,
          commissionNo: commission.commissionNo,
          administratorId: commission.administratorId,
          administrator: `${commission.administrator.firstName} ${commission.administrator.lastName}`.trim() || '—',
          amount,
          paidAmount:
            commission.paymentTransaction?.status === 'PAID'
              ? Number(commission.paymentTransaction.amount)
              : 0,
          recoveryOutstanding,
          recoveryDeduction,
          netPaymentAmount: amount - recoveryDeduction,
          commissionRate: Number(commission.commissionRate ?? 0),
          pax: commission.pax ?? 0,
          status: commission.status,
          note: `อัตราคอมมิชชั่น ${Number(commission.commissionRate ?? 0).toLocaleString('th-TH')}% · ${commission.pax ?? 0} คน`,
          recoveries: commission.recoveryApplications.map((recoveryApplication) => {
            const recovery = recoveryApplication.recovery;
            return {
            id: recovery.id,
            bookingNo: recovery.booking.bookingNo,
            refundNo: recovery.bookingRefund.refundNo,
            appliedAmount: Number(recoveryApplication.amount),
            recoveryAmount: Number(recovery.recoveryAmount),
            settledAmount: Number(recovery.settledAmount),
            remainingAmount: Number(recovery.remainingAmount),
            status: recovery.status,
            reason: recovery.reason,
            createdAt: new Date(recovery.createdAt),
            applications: recovery.applications.map((application) => ({
              id: application.id,
              targetCode: application.bookingCommission?.commissionNo ?? application.tourCommission?.commissionNo ?? '-',
              amount: Number(application.amount),
              status: application.status,
            })),
          };
          }),
        };
        }),
      );
      this.advanceApprovals.set(adjustments.map((adjustment: TourFinancialAdjustmentItem) => ({
        id: adjustment.id,
        requestNo: adjustment.adjustmentNo,
        requestedAt: adjustment.createdAt,
        requestedBy: `${adjustment.requestedBy.firstName} ${adjustment.requestedBy.lastName}`.trim() || '—',
        reason: adjustment.reason,
        amount: Number(adjustment.tourPriceDelta) + Number(adjustment.expenseBudgetDelta) + Number(adjustment.customerChargeDelta) + Number(adjustment.companyAdvanceAmount),
        tourPriceDelta: Number(adjustment.tourPriceDelta),
        expenseBudgetDelta: Number(adjustment.expenseBudgetDelta),
        customerChargeDelta: Number(adjustment.customerChargeDelta),
        companyAdvanceAmount: Number(adjustment.companyAdvanceAmount),
        advanceAttachmentPath: adjustment.advanceAttachmentPath,
        approver: null,
        status: adjustment.status,
      })));
    } catch (error: unknown) {
      this.tour.set(null);
      this.overlay.error(
        'โหลดรายละเอียดทัวร์ไม่สำเร็จ',
        this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'),
      );
    } finally {
      this.overlay.hideLoading();
    }
  }

  canModifyBooking(row: TourBookingView): boolean {
    if (this.isTourClosed()) return false;
    const booking = this.accessibleBookings().find((item) => item.id === row.id);
    return !!booking && canModifyBooking(this.auth.user(), booking.booking_payments.length > 0);
  }

  isBookingDeleted(row: TourBookingView): boolean {
    return this.accessibleBookings().find((item) => item.id === row.id)?.status === 'DELETED';
  }

  openEditBookingModal(row: TourBookingView): void {
    if (!this.canModifyBooking(row) || this.isBookingDeleted(row)) return;
    const booking = this.accessibleBookings().find((item) => item.id === row.id);
    if (!booking?.tourId) return;
    this.domesticEditForm.reset({
      customerId: booking.booking_customer.id,
      travelerCount: booking.pax,
      discount: Number(booking.discount ?? 0),
    });
    this.domesticCustomerOptions.set([{ label: booking.booking_customer.name, value: booking.booking_customer.id }]);
    this.editingBooking.set(booking);
    void this.loadDomesticCustomers();
  }

  @HostListener('document:keydown.escape')
  closeDomesticEditModal(): void {
    if (!this.isSavingDomesticEdit()) this.editingBooking.set(null);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.isRefreshButtonHidden.set(window.scrollY <= 16);
  }

  private async loadDomesticCustomers(): Promise<void> {
    try {
      const customers = await this.customerService.getActiveOptions();
      this.domesticCustomerOptions.set(customers.map((customer) => ({ label: customer.name, value: customer.id })));
    } catch {
      this.overlay.error('โหลดรายชื่อลูกค้าไม่สำเร็จ', 'กรุณาปิดหน้าต่างแล้วลองใหม่อีกครั้ง');
    }
  }

  async saveDomesticBookingChanges(): Promise<void> {
    const booking = this.editingBooking();
    if (!booking) return;
    if (this.isTourClosed() || this.isSavingDomesticEdit() || !booking.tourId || booking.status === 'DELETED' ||
        !canModifyBooking(this.auth.user(), booking.booking_payments.length > 0)) return;
    this.domesticEditForm.markAllAsTouched();
    if (this.domesticEditForm.invalid) return;
    const value = this.domesticEditForm.getRawValue();
    if (value.discount > Number(booking.pricePerPerson) * value.travelerCount) {
      this.domesticEditForm.controls.discount.setErrors({ discountExceeded: true });
      return;
    }
    this.isSavingDomesticEdit.set(true);
    this.overlay.showLoading('กำลังบันทึกการจองทัวร์ภายใน...');
    try {
      if (booking.bookingType === 'GROUP_TOUR') {
        await this.groupBookingService.updateGroupBooking(booking.id, {
          tourId: booking.tourId ?? undefined,
          customerId: value.customerId,
          customerIsTraveler: false,
          tourName: booking.tourName,
          tourCode: booking.tourCode ?? '',
          country: booking.country ?? '',
          departureDate: booking.departureDate.slice(0, 10),
          returnDate: (booking.returnDate ?? booking.departureDate).slice(0, 10),
          pax: value.travelerCount,
          tourPrice: Number(booking.pricePerPerson),
          discount: value.discount,
        });
      } else {
        await this.bookingService.updateDomestic(booking.id, {
          customerId: value.customerId, pax: value.travelerCount, discount: value.discount,
        });
      }
      this.overlay.success(
        booking.bookingType === 'GROUP_TOUR' ? 'แก้ไข Booking เหมากรุ๊ปสำเร็จ' : 'แก้ไขการจองทัวร์ภายในสำเร็จ',
        `บันทึก ${booking.bookingNo} เรียบร้อยแล้ว`,
      );
      this.editingBooking.set(null);
      const tourId = this.tour()?.id;
      if (tourId) await this.loadTour(tourId);
    } catch (error: unknown) {
      const message = error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('แก้ไขการจองทัวร์ภายในไม่สำเร็จ', message);
    } finally {
      this.isSavingDomesticEdit.set(false);
      this.overlay.hideLoading();
    }
  }

  async deleteBooking(row: TourBookingView): Promise<void> {
    if (this.isDeletingBooking() || !this.canModifyBooking(row) || this.isBookingDeleted(row)) return;
    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบ Booking',
      message: `ต้องการลบ ${row.bookingNo} ใช่หรือไม่? รายการจะถูกเปลี่ยนสถานะเป็นลบแล้ว`,
      confirmText: 'ลบ Booking', cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;
    this.isDeletingBooking.set(true);
    this.overlay.showLoading('กำลังลบ Booking...');
    try {
      const booking = this.accessibleBookings().find((item) => item.id === row.id);
      if (booking?.bookingType === 'GROUP_TOUR') {
        await this.groupBookingService.deleteGroupBooking(row.id);
      } else {
        await this.bookingService.delete(row.id);
      }
      const tourId = this.tour()?.id;
      if (tourId) await this.loadTour(tourId);
      this.overlay.success('ลบ Booking สำเร็จ', 'เปลี่ยนสถานะ Booking เป็นลบแล้ว');
    } catch (error: unknown) {
      this.overlay.error('ลบ Booking ไม่สำเร็จ', this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'));
    } finally {
      this.isDeletingBooking.set(false);
      this.overlay.hideLoading();
    }
  }

  private isRefundBooking(booking: { status: string }): boolean {
    return booking.status === 'REFUND';
  }

  private toTourDetailView(tour: TourItem, expenses: TourExpenseItem[]): TourDetailView {
    return {
      id: tour.id,
      tourNo: tour.tourNo,
      code: tour.tourCode,
      tourName: tour.tourName,
      type: tour.tourType,
      travelDate: tour.travelDate,
      returnDate: tour.returnDate,
      requiredPax: tour.requiredPax ?? 0,
      currentPax: tour.bookings
        .filter((booking) => !['DELETED', 'REFUND'].includes(booking.status))
        .reduce((total, booking) => total + booking.pax, 0),
      bookingCount: tour.bookings.filter(
        (booking) => !['DELETED', 'REFUND'].includes(booking.status),
      ).length,
      tourPrice: Number(tour.tourprice),
      tourTotalAmount: Number(tour.tourTotalAmount),
      estimatedExpense: Number(tour.estimatedExpense),
      saleCommission: Number(tour.com_sale),
      adminCommissionPercent: Number(tour.com_admintrator),
      programDetail: tour.programDetail,
      note: tour.note,
      profileUrl: this.toAssetUrl(tour.profilePath),
      pdfUrl: this.toAssetUrl(tour.tourPdfDetailPath),
      status: tour.status,
      createdById: tour.createdById,
      createdByName: tour.createdBy
        ? `${tour.createdBy.firstName} ${tour.createdBy.lastName}`.trim()
        : '—',
      administrators: tour.administrators.map((administrator) =>
        this.toAdministratorView(administrator),
      ),
      expenses: expenses.map((expense) => {
        const estimatedAmount = Number(expense.amount ?? 0);
        const actualTransferAmount =
          expense.actualTransferAmount === null ? null : Number(expense.actualTransferAmount);
        return {
          id: expense.id,
          expenseNo: expense.expenseNo,
          name: expense.expenseNameSnapshot,
          description: expense.description,
          estimatedAmount,
          actualTransferAmount,
          paidAmount: expense.paymentTransactions.reduce(
            (total, payment) => total + Number(payment.amount),
            0,
          ),
          amount: estimatedAmount,
          attachmentUrl: this.toAssetUrl(expense.attachmentPath),
          receiptUrl: this.toAssetUrl(expense.receiptPath ?? expense.receiptUrl),
          requester:
            `${expense.requestedBy.firstName} ${expense.requestedBy.lastName}`.trim() || '—',
          department: expense.requestedBy.department.name,
          createdAt: expense.createdAt,
          source: expense.source,
          status: expense.status,
        };
      }),
      bookings: tour.bookings
        .filter((booking) => booking.status !== 'DELETED')
        .map((booking) => ({
        id: booking.id,
        bookingNo: booking.bookingNo,
        customerName: booking.booking_customer.name,
        bookingType: booking.bookingType,
        pax: booking.pax,
        totalAmount: Number(booking.totalAmount),
        discount: Number(booking.discount ?? 0),
        paidAmount: booking.booking_payments.reduce(
          (total, payment) => total + Number(payment.customerPaidAmount ?? 0),
          0,
        ),
        withholdingTaxAmount: booking.booking_payments.reduce(
          (total, payment) => total + Number(payment.withholdingTaxAmount ?? 0),
          0,
        ),
        customerCreditCardFee: booking.booking_payments.reduce(
          (total, payment) =>
            payment.status === 'VERIFIED' && payment.creditCardFeeResponsibility === 'CUSTOMER'
              ? total + Number(payment.creditCardFeeAmount ?? 0)
              : total,
          0,
        ),
        companyCreditCardFee: booking.booking_payments.reduce(
          (total, payment) =>
            payment.status === 'VERIFIED' && payment.creditCardFeeResponsibility === 'COMPANY'
              ? total + Number(payment.creditCardFeeAmount ?? 0)
              : total,
          0,
        ),
        refundedAmount: booking.booking_refunds.reduce(
          (total, refund) =>
            refund.status === 'PAID' && refund.paymentTransaction?.status === 'PAID'
              ? total + Number(refund.paymentTransaction.amount)
              : total,
          0,
        ),
        activeRefundAmount: booking.booking_refunds.reduce(
          (total, refund) =>
            ['REJECTED', 'CANCELLED'].includes(refund.status)
              ? total
              : total + Number(refund.amount),
          0,
        ),
        status: booking.status,
        paymentStatus: booking.paymentStatus,
        })),
    };
  }

  private toAssetUrl(path: string | null): string | null {
    if (!path) return null;
    if (/^(https?:)?\/\//.test(path)) return path;
    return `${this.assetBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  private fileNameFromPath(path: string | null): string {
    return path?.split('/').pop() ?? '';
  }

  async openAddAdministrator(): Promise<void> {
    if (!this.canAddTourAdministrator()) return;
    this.administratorForm.reset({ userId: '', responsibility: '', isPrimary: false });
    this.isAddAdministratorOpen.set(true);
    await this.loadAdministratorOptions();
  }

  closeAddAdministrator(): void {
    this.isAddAdministratorOpen.set(false);
  }

  async submitAddAdministrator(): Promise<void> {
    if (!this.canAddTourAdministrator() || this.isSavingAdministrator() || this.administratorForm.invalid) {
      this.administratorForm.markAllAsTouched();
      return;
    }

    const tour = this.tour();
    if (!tour) return;

    const value = this.administratorForm.getRawValue();
    this.isSavingAdministrator.set(true);
    this.overlay.showLoading('กำลังบันทึกผู้ดูแลทัวร์...');
    try {
      const administrator = await this.tourService.createAdministrator(tour.id, {
        userId: value.userId,
        responsibility: value.responsibility.trim() || null,
        isPrimary: value.isPrimary,
      });
      const administratorView = this.toAdministratorView(administrator);
      this.tour.update((currentTour) =>
        currentTour
          ? {
              ...currentTour,
              administrators: [
                ...currentTour.administrators.map((item) => ({
                  ...item,
                  isPrimary: administrator.isPrimary ? false : item.isPrimary,
                })),
                administratorView,
              ],
            }
          : currentTour,
      );
      this.administratorOptions.update((options) =>
        options.filter((option) => option.value !== administrator.userId),
      );
      this.closeAddAdministrator();
      this.overlay.success('เพิ่มผู้ดูแลสำเร็จ', 'บันทึกผู้ดูแลทัวร์เรียบร้อยแล้ว');
    } catch (error: unknown) {
      this.overlay.error(
        'เพิ่มผู้ดูแลไม่สำเร็จ',
        this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'),
      );
    } finally {
      this.isSavingAdministrator.set(false);
    }
  }

  private async loadAdministratorOptions(): Promise<void> {
    if (this.isLoadingAdministratorOptions()) return;

    this.isLoadingAdministratorOptions.set(true);
    try {
      const assignedUserIds = new Set(
        (this.tour()?.administrators ?? []).map((administrator) => administrator.userId),
      );
      const users = await this.tourService.getAdministratorOptions();
      this.administratorOptions.set(
        users
          .filter((user) => !assignedUserIds.has(user.id))
          .map((user) => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName} (${user.employeeCode} · ${user.department.name})`,
          })),
      );
    } catch (error: unknown) {
      this.administratorOptions.set([]);
      this.overlay.error(
        'โหลดรายชื่อผู้ดูแลไม่สำเร็จ',
        this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'),
      );
    } finally {
      this.isLoadingAdministratorOptions.set(false);
    }
  }

  private toAdministratorView(administrator: TourAdministratorItem): TourAdministratorView {
    return {
      id: administrator.id,
      userId: administrator.userId,
      name: `${administrator.user.firstName} ${administrator.user.lastName}`.trim(),
      responsibility: administrator.responsibility ?? 'ผู้ดูแลทัวร์',
      isPrimary: administrator.isPrimary,
    };
  }

  async deleteAdministrator(administrator: TourAdministratorView): Promise<void> {
    const tour = this.tour();
    if (!tour || !this.canManagePrimaryTourData()) return;

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบผู้ดูแล',
      message: `ต้องการลบ ${administrator.name} ออกจากผู้ดูแลทัวร์ใช่หรือไม่?`,
      confirmText: 'ลบผู้ดูแล',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังลบผู้ดูแลทัวร์...');
    try {
      const deletedAdministrator = await this.tourService.deleteAdministrator(
        tour.id,
        administrator.id,
      );
      this.tour.update((currentTour) =>
        currentTour
          ? {
              ...currentTour,
              administrators: currentTour.administrators.filter(
                (item) => item.id !== deletedAdministrator.id,
              ),
            }
          : currentTour,
      );
      this.overlay.success('ลบผู้ดูแลสำเร็จ', `นำ ${administrator.name} ออกจากทัวร์แล้ว`);
    } catch (error: unknown) {
      this.overlay.error('ลบผู้ดูแลไม่สำเร็จ', this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'));
    }
  }

  openExpenseModal(): void {
    if (!this.canCreateExpense()) return;

    this.editingExpenseId.set(null);
    this.editingExpenseSource.set(null);
    this.expenseForm.reset({
      expenseNameSnapshot: '',
      estimatedAmount: null,
      actualTransferAmount: null,
      description: '',
    });
    this.setExpenseAmountValidator();
    this.purchaseOrderFileName.set('');
    this.purchaseOrderFile.set(null);
    this.existingPurchaseOrderUrl.set(null);
    this.isExpenseModalOpen.set(true);
    void this.loadExpenseTypes();
  }

  editExpense(expense: TourExpenseView): void {
    if (!this.canManageTourExpenses() || expense.status !== 'DRAFT') return;

    this.editingExpenseId.set(expense.id);
    this.editingExpenseSource.set(expense.source);
    this.expenseForm.reset({
      expenseNameSnapshot: expense.name,
      estimatedAmount: expense.estimatedAmount,
      actualTransferAmount: expense.actualTransferAmount,
      description: expense.description,
    });
    this.purchaseOrderFile.set(null);
    this.existingPurchaseOrderUrl.set(expense.attachmentUrl);
    this.purchaseOrderFileName.set(this.fileNameFromPath(expense.attachmentUrl));
    this.setExpenseAmountValidator();
    this.isExpenseModalOpen.set(true);
    void this.loadExpenseTypes();
  }

  async deleteExpense(expense: TourExpenseView): Promise<void> {
    const tour = this.tour();
    if (
      !tour ||
      this.isTourClosed() ||
      !this.canManagePrimaryTourData() ||
      expense.status !== 'REJECTED' ||
      expense.source === 'TOUR_ESTIMATE'
    ) return;

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบใบขอเบิก',
      message: `ต้องการลบ ${expense.expenseNo} หรือไม่`,
      confirmText: 'ลบรายการ',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังลบใบขอเบิก...');
    try {
      await this.tourService.deleteExpense(tour.id, expense.id);
      await this.loadTour(tour.id);
      this.overlay.success('ลบใบขอเบิกสำเร็จ');
    } catch (error: unknown) {
      this.overlay.error('ลบใบขอเบิกไม่สำเร็จ', this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'));
    } finally {
      this.overlay.hideLoading();
    }
  }

  private async loadExpenseTypes(): Promise<void> {
    try {
      const expenseTypes = await this.tourService.getExpenseTypes();
      this.expenseTypeOptions.set(expenseTypes.map((expenseType) => expenseType.name));
    } catch {
      this.expenseTypeOptions.set([]);
    }
  }

  closeExpenseModal(): void {
    this.isExpenseModalOpen.set(false);
    this.editingExpenseId.set(null);
  }

  selectPurchaseOrder(event: FileSelectEvent): void {
    const file = event.files?.[0] ?? null;
    this.purchaseOrderFile.set(file);
    this.purchaseOrderFileName.set(file?.name ?? this.fileNameFromPath(this.existingPurchaseOrderUrl()));
  }

  clearPurchaseOrder(): void {
    this.purchaseOrderFile.set(null);
    this.purchaseOrderFileName.set(this.fileNameFromPath(this.existingPurchaseOrderUrl()));
  }

  onExpenseTypeChange(): void {
    if (!this.isMiscellaneousExpense()) return;
    this.purchaseOrderFile.set(null);
    this.purchaseOrderFileName.set('');
  }

  isMiscellaneousExpense(): boolean {
    const expenseName = this.expenseForm.controls.expenseNameSnapshot.value
      .normalize('NFKC')
      .replace(/\s+/g, ' ')
      .trim();
    return expenseName === 'ค่าใช้จ่ายเบ็ดเตล็ด' || expenseName === 'ค่าใช้จ่ายเบ็ตเตล็ด';
  }

  openExpenseReceiptModal(expense: TourExpenseView): void {
    if (!this.canManageTourExpenses()) return;
    this.selectedReceiptExpense.set(expense);
    this.expenseReceiptFile.set(null);
    this.expenseReceiptFileName.set('');
  }

  closeExpenseReceiptModal(): void {
    if (this.isSavingExpenseReceipt()) return;
    this.selectedReceiptExpense.set(null);
    this.expenseReceiptFile.set(null);
    this.expenseReceiptFileName.set('');
  }

  selectExpenseReceipt(event: FileSelectEvent): void {
    const file = event.files?.[0] ?? null;
    this.expenseReceiptFile.set(file);
    this.expenseReceiptFileName.set(file?.name ?? '');
  }

  async saveExpenseReceipt(): Promise<void> {
    const tour = this.tour();
    const expense = this.selectedReceiptExpense();
    const receipt = this.expenseReceiptFile();
    if (!tour || this.isTourClosed() || !expense || !receipt || this.isSavingExpenseReceipt()) return;

    this.isSavingExpenseReceipt.set(true);
    try {
      await this.tourService.submitExpenseReceipt(tour.id, expense.id, receipt);
      this.selectedReceiptExpense.set(null);
      this.expenseReceiptFile.set(null);
      this.expenseReceiptFileName.set('');
      await this.loadTour(tour.id);
      this.overlay.success('ส่งใบเสร็จสำเร็จ');
    } catch (error: unknown) {
      this.overlay.error('ส่งใบเสร็จไม่สำเร็จ', this.apiErrorMessage(error, 'ไม่สามารถส่งใบเสร็จได้'));
    } finally {
      this.isSavingExpenseReceipt.set(false);
    }
  }

  isExpenseFieldInvalid(field: keyof typeof this.expenseForm.controls): boolean {
    const control = this.expenseForm.controls[field];
    return control.touched && control.invalid;
  }

  private setExpenseAmountValidator(): void {
    this.expenseForm.controls.actualTransferAmount.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(this.expenseActualTransferLimit()),
    ]);
    this.expenseForm.controls.actualTransferAmount.updateValueAndValidity();
  }

  editableExpenseAmount(): number {
    const editingExpense = this.editingExpenseId()
      ? this.tour()?.expenses.find((expense) => expense.id === this.editingExpenseId())
      : undefined;
    return this.availableExpenseAmount() + (editingExpense ? this.expenseFundingAmount(editingExpense) : 0);
  }

  editableExpenseBudget(): number {
    const editingExpense = this.editingExpenseId()
      ? this.tour()?.expenses.find((expense) => expense.id === this.editingExpenseId())
      : undefined;
    return this.expenseBudgetRemaining() + (editingExpense ? this.expenseEffectiveAmount(editingExpense) : 0);
  }

  expenseActualTransferLimit(): number {
    const editableLimit = Math.min(this.editableExpenseAmount(), this.editableExpenseBudget());
    const editingExpense = this.editingExpenseId()
      ? this.tour()?.expenses.find((expense) => expense.id === this.editingExpenseId())
      : undefined;
    if (editingExpense?.source !== 'TOUR_ESTIMATE') return editableLimit;
    return Math.min(editableLimit, editingExpense.amount);
  }

  expenseEffectiveAmount(expense: TourExpenseView): number {
    return expense.actualTransferAmount ?? expense.amount;
  }

  expenseFundingAmount(expense: TourExpenseView): number {
    return expense.actualTransferAmount ?? 0;
  }

  checkExpenseAmount(value: number | string | null | undefined): void {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount) || amount <= this.expenseActualTransferLimit()) return;
    this.expenseForm.controls.actualTransferAmount.markAsTouched();
    this.expenseForm.controls.actualTransferAmount.updateValueAndValidity({ emitEvent: false });
  }

  expenseActualTransferAmountExceeded(): boolean {
    const amount = Number(this.expenseForm.controls.actualTransferAmount.value ?? 0);
    return amount > 0 && amount > this.expenseActualTransferLimit();
  }

  async submitExpense(submit: boolean): Promise<void> {
    if (this.isSavingExpense()) return;
    if (this.isTourClosed()) return;
    if (!this.editingExpenseId() && !this.canCreateExpense()) return;
    if (
      this.expenseForm.invalid ||
      (!this.isMiscellaneousExpense() &&
        !this.purchaseOrderFile() &&
        !this.existingPurchaseOrderUrl())
    ) {
      this.expenseForm.markAllAsTouched();
      return;
    }
    if ((this.expenseForm.controls.actualTransferAmount.value ?? 0) > this.expenseActualTransferLimit()) {
      this.expenseForm.controls.actualTransferAmount.markAsTouched();
      this.expenseForm.controls.actualTransferAmount.updateValueAndValidity({ emitEvent: false });
      return;
    }
    const tour = this.tour();
    if (!tour) return;

    const value = this.expenseForm.getRawValue();
    if (value.actualTransferAmount === null) return;
    this.isSavingExpense.set(true);
    this.overlay.showLoading(submit ? 'กำลังส่งใบขอเบิก...' : 'กำลังบันทึกใบขอเบิก...');
    try {
      const input = {
        expenseNameSnapshot: value.expenseNameSnapshot,
        description: value.description,
        actualTransferAmount: value.actualTransferAmount,
        submitForReview: submit,
      };
      const editingExpenseId = this.editingExpenseId();
      if (editingExpenseId) {
        await this.tourService.updateExpense(tour.id, editingExpenseId, input, this.purchaseOrderFile());
      } else {
        const attachment = this.purchaseOrderFile();
        await this.tourService.createExpense(tour.id, input, attachment);
      }
      this.closeExpenseModal();
      await this.loadTour(tour.id);
      this.overlay.success(
        submit ? 'ส่งใบขอเบิกสำเร็จ' : 'บันทึกใบขอเบิกสำเร็จ',
        submit ? 'ส่งรายการให้หัวหน้าตรวจสอบแล้ว' : 'บันทึกใบขอเบิกเป็นร่างแล้ว',
      );
    } catch (error: unknown) {
      this.overlay.error('บันทึกใบขอเบิกไม่สำเร็จ', this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'));
    } finally {
      this.isSavingExpense.set(false);
      this.overlay.hideLoading();
    }
  }

  openFinancialAdjustmentModal(): void {
    if (!this.canManageTourExpenses()) return;
    this.financialAdjustmentForm.reset({
      expenseBudgetDelta: null,
      adjustExpenseBudget: false,
      adjustBookingCharges: false,
      adjustTourPrice: false,
      tourPriceDelta: null,
      requestAdvance: false,
      companyAdvanceAmount: null,
      reason: '',
    });
    this.bookingChargeItems.clear();
    for (const booking of this.visibleBookings().filter((booking) => !this.isRefundBooking(booking))) {
      this.bookingChargeItems.push(this.createBookingChargeForm(booking));
    }
    this.financialAdjustmentSubmitted.set(false);
    this.advanceAttachmentFileName.set('');
    this.advanceAttachmentFile.set(null);
    this.isFinancialAdjustmentModalOpen.set(true);
  }

  closeFinancialAdjustmentModal(): void {
    this.isFinancialAdjustmentModalOpen.set(false);
  }

  selectAdvanceAttachment(event: FileSelectEvent): void {
    const file = event.files?.[0] ?? null;
    this.advanceAttachmentFile.set(file);
    this.advanceAttachmentFileName.set(file?.name ?? '');
  }

  readonly financialAdjustmentSubmitted = signal(false);
  readonly isSavingFinancialAdjustment = signal(false);

  private createBookingChargeForm(booking: TourBookingView) {
    return this.fb.nonNullable.group({
      bookingId: [booking.id],
      bookingNo: [booking.bookingNo],
      customerName: [booking.customerName],
      pax: [booking.pax],
      selected: [false],
      amount: this.fb.control<number | null>(null),
    });
  }

  get bookingChargeItems() {
    return this.financialAdjustmentForm.controls.bookingCharges;
  }

  get adjustmentBookingTotal(): number {
    if (!this.financialAdjustmentForm.controls.adjustBookingCharges.value) return 0;
    return this.bookingChargeItems.controls.reduce((sum, row) =>
      sum + (row.controls.selected.value ? this.bookingChargeTotal(row.getRawValue()) : 0), 0);
  }

  bookingChargeTotal(row: { amount: number | null }): number {
    return row.amount ?? 0;
  }

  get proposedTourTotalAmount(): number {
    const controls = this.financialAdjustmentForm.controls;
    return (this.tour()?.tourTotalAmount ?? 0) +
      (controls.adjustTourPrice.value ? Number(controls.tourPriceDelta.value ?? 0) : 0);
  }

  get adjustmentExpenseTotal(): number {
    const controls = this.financialAdjustmentForm.controls;
    return controls.adjustExpenseBudget.value
      ? Number(controls.expenseBudgetDelta.value ?? 0)
      : 0;
  }

  get expenseBudgetExceedsNetSales(): boolean {
    return this.adjustmentExpenseTotal > this.bookingNetSalesTotal();
  }

  get adjustmentAdvanceTotal(): number {
    const controls = this.financialAdjustmentForm.controls;
    return controls.requestAdvance.value ? Number(controls.companyAdvanceAmount.value ?? 0) : 0;
  }

  get isFinancialAdjustmentValid(): boolean {
    const controls = this.financialAdjustmentForm.controls;
    const selectedBookings = this.bookingChargeItems.controls.filter(row => row.controls.selected.value);
    return !!controls.reason.value.trim() &&
      (controls.adjustTourPrice.value || controls.adjustExpenseBudget.value || controls.adjustBookingCharges.value || controls.requestAdvance.value) &&
      (!controls.adjustTourPrice.value || Number(controls.tourPriceDelta.value ?? 0) > 0) &&
      (!controls.adjustExpenseBudget.value || (this.adjustmentExpenseTotal > 0 && !this.expenseBudgetExceedsNetSales)) &&
      (!controls.requestAdvance.value || this.adjustmentAdvanceTotal > 0) &&
      (!controls.requestAdvance.value || !!this.advanceAttachmentFile()) &&
      (!controls.adjustBookingCharges.value || (selectedBookings.length > 0 &&
        selectedBookings.every(row => this.bookingChargeTotal(row.getRawValue()) > 0)));
  }

  submitFinancialAdjustment(): void {
    if (!this.canManageTourExpenses()) return;
    this.financialAdjustmentSubmitted.set(true);
    this.financialAdjustmentForm.markAllAsTouched();
    if (!this.isFinancialAdjustmentValid) return;
    const tourId = this.route.snapshot.paramMap.get('id');
    if (!tourId || this.isSavingFinancialAdjustment()) return;

    const controls = this.financialAdjustmentForm.controls;
    const tourPriceDelta = controls.adjustTourPrice.value ? Number(controls.tourPriceDelta.value ?? 0) : 0;
    const expenseBudgetDelta = controls.adjustExpenseBudget.value ? Number(controls.expenseBudgetDelta.value ?? 0) : 0;
    const companyAdvanceAmount = controls.requestAdvance.value ? Number(controls.companyAdvanceAmount.value ?? 0) : 0;
    const selectedBookings = this.bookingChargeItems.controls
      .filter((row) => row.controls.selected.value)
      .map((row) => ({ bookingId: row.controls.bookingId.value, amount: this.bookingChargeTotal(row.getRawValue()) }));
    const requests: TourFinancialAdjustmentRequest[] = [];
    const canCombineWithSingleBooking = selectedBookings.length === 1;

    if (tourPriceDelta > 0 || expenseBudgetDelta > 0 || companyAdvanceAmount > 0 || canCombineWithSingleBooking) {
      requests.push({
        tourPriceDelta,
        expenseBudgetDelta,
        customerChargeDelta: canCombineWithSingleBooking ? selectedBookings[0].amount : 0,
        companyAdvanceAmount,
        reason: controls.reason.value.trim(),
        ...(canCombineWithSingleBooking ? { bookingId: selectedBookings[0].bookingId } : {}),
      });
    }
    if (!canCombineWithSingleBooking) {
      for (const booking of selectedBookings) {
        requests.push({
          tourPriceDelta: 0,
          expenseBudgetDelta: 0,
          customerChargeDelta: booking.amount,
          companyAdvanceAmount: 0,
          reason: controls.reason.value.trim(),
          bookingId: booking.bookingId,
        });
      }
    }

    this.isSavingFinancialAdjustment.set(true);
    this.overlay.showLoading('กำลังบันทึกคำขอปรับยอดทัวร์...');
    void Promise.all(
      requests.map((request, index) =>
        this.tourService.createFinancialAdjustment(
          tourId,
          request,
          index === 0 ? this.advanceAttachmentFile() : null,
        ),
      ),
    )
      .then(async () => {
        this.closeFinancialAdjustmentModal();
        await this.loadTour(tourId);
        this.overlay.success('บันทึกคำขอสำเร็จ', 'ส่งคำขอปรับยอดทัวร์เพื่อรออนุมัติแล้ว');
      })
      .catch((error: unknown) => {
        this.overlay.error('บันทึกคำขอไม่สำเร็จ', this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'));
      })
      .finally(() => {
        this.isSavingFinancialAdjustment.set(false);
        this.overlay.hideLoading();
      });
  }

  private apiErrorMessage(error: unknown, fallback: string): string {
    return error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
      ? error.error.message
      : fallback;
  }
}
